import { OrderEntityType, OrderInvoiceStatus, OrderStatus, OrderType } from "~/gql/graphqlBase.generated";

export interface InvoiceStandingFields {
	invoiceStatus?: OrderInvoiceStatus | null;
	orderType?: null | OrderType;
}

export interface InvoiceEligibilityFields extends InvoiceStandingFields {
	orderEntityType: OrderEntityType;
	status: OrderStatus;
}

const REBILLABLE_INVOICE_STATUSES: ReadonlySet<OrderInvoiceStatus> = new Set([
	OrderInvoiceStatus.NotInvoiced,
	OrderInvoiceStatus.Voided
]);

export const INVOICEABLE_STATUSES: ReadonlySet<OrderStatus> = new Set([
	OrderStatus.Closed,
	OrderStatus.Delivered,
	OrderStatus.FailedDelivery,
	OrderStatus.InTransit,
	OrderStatus.Shipped
]);

const CREDIT_MEMO_ISSUABLE_STATUSES: ReadonlySet<OrderStatus> = new Set([
	OrderStatus.Closed,
	OrderStatus.Delivered,
	OrderStatus.InTransit,
	OrderStatus.Shipped,
	OrderStatus.Synchronized
]);

const BPF_INVOICED_STATUSES: ReadonlySet<OrderStatus> = new Set([
	OrderStatus.Closed,
	OrderStatus.Delivered,
	OrderStatus.FailedDelivery,
	OrderStatus.InTransit,
	OrderStatus.Shipped,
	OrderStatus.Synchronized
]);

export const hasActiveInvoice = (order: InvoiceStandingFields): boolean =>
	order.invoiceStatus === OrderInvoiceStatus.Invoiced;

export const isBpfTenant = (tenantId?: string | null): boolean => tenantId === "BPF";

export const hasBpfInvoice = (status: OrderStatus): boolean => BPF_INVOICED_STATUSES.has(status);

export const isAwaitedInvoiceLive = (
	awaitingOrderId: string | null,
	order: InvoiceStandingFields & { orderId?: string }
): boolean => awaitingOrderId !== null && order.orderId === awaitingOrderId && hasActiveInvoice(order);

export const isCreditMemoOrder = (order: InvoiceStandingFields): boolean =>
	order.orderType === OrderType.Credit || order.invoiceStatus === OrderInvoiceStatus.CreditMemo;

export const isRebillable = (order: InvoiceStandingFields): boolean =>
	!isCreditMemoOrder(order) && order.invoiceStatus != null && REBILLABLE_INVOICE_STATUSES.has(order.invoiceStatus);

export const isInvoiceableStatus = (status: OrderStatus): boolean => INVOICEABLE_STATUSES.has(status);

export const isCreditMemoIssuableStatus = (status: OrderStatus): boolean => CREDIT_MEMO_ISSUABLE_STATUSES.has(status);

export const isBulkPromptInvoiceableNow = (order: InvoiceEligibilityFields): boolean =>
	order.orderEntityType === OrderEntityType.Order && order.status === OrderStatus.Shipped && isRebillable(order);

export const isInvoiceableNow = (order: InvoiceEligibilityFields): boolean =>
	order.orderEntityType === OrderEntityType.Order && isInvoiceableStatus(order.status) && isRebillable(order);

export const markOrdersInvoiced = <TOrder extends InvoiceStandingFields & { orderEntityId: string }>(
	orders: ReadonlyArray<TOrder>,
	invoicedOrderIds: ReadonlySet<string>
): TOrder[] =>
	orders.map((order) =>
		invoicedOrderIds.has(order.orderEntityId) ? { ...order, invoiceStatus: OrderInvoiceStatus.Invoiced } : order
	);

export interface InvoiceSelectionPartition<TOrder> {
	invoiceableNow: TOrder[];
	invoiced: TOrder[];
	other: TOrder[];
}

const partitionInvoiceSelection = <TOrder extends InvoiceEligibilityFields>(
	orders: ReadonlyArray<TOrder>,
	isBpf: boolean
): InvoiceSelectionPartition<TOrder> => {
	const partition: InvoiceSelectionPartition<TOrder> = { invoiceableNow: [], invoiced: [], other: [] };

	for (const order of orders) {
		if (order.orderEntityType !== OrderEntityType.Order) {
			partition.other.push(order);
		} else if (isBpf ? hasBpfInvoice(order.status) : hasActiveInvoice(order)) {
			partition.invoiced.push(order);
		} else if (isBulkPromptInvoiceableNow(order)) {
			partition.invoiceableNow.push(order);
		} else {
			partition.other.push(order);
		}
	}

	return partition;
};

export default partitionInvoiceSelection;
